// src/pages/SiteManagement.js - Updated with controlled inputs
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Navigate, useLocation } from "react-router-dom";
import api from "../../../services/apiClient";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import ReportTable from "../../common/ReportTable/ReportTable";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter from "../../common/DateRangeFilter";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import "./studymanagement.scss";
import "../../common/StudyAssignedStaffPanel/StudyAssignedStaffPanel.scss";
import Dropdown from "../../common/dropDown";

//utilities
import utils from "../../../utils/utils";
import { getInitials } from "../../../utils/avatarUtils";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
} from "../../../utils/permissionUtils";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import StudyFormSection from "./StudyFormSection";
import CapaCreateModal from "../capa/CapaCreateModal";
import {
  buildCapaContextFromStudy,
  studyRowCapaDisplayLabel,
  studyRowCapaId,
} from "../capa/capaConstants";
import {
  getActiveSiteLookup,
  getInternalActiveStaffLookup,
  siteToDropdownOptions,
  staffToDropdownOptions,
  staffMappedToAnySite,
} from "../../../services/lookupService";
import StudyAssignedStaffPanel from "../../common/StudyAssignedStaffPanel";
import {
  StudyDescriptionField,
  StudyEndDateField,
  StudyProtocolNumberField,
  StudyProtocolVersionField,
  StudyStartDateField,
  StudyTypeField,
} from "./StudyMetadataFields";
import {
  EMPTY_STUDY_METADATA,
  buildStudyMetadataPayload,
  getStudyMetadataValidationErrors,
  mapStudyMetadataFromInfo,
} from "./studyFormMetadataUtils";
import StudyGateBadge from "../../common/StudyGateBadge/StudyGateBadge";
import StudyGateAdvanceModal from "../../common/StudyGateAdvanceModal/StudyGateAdvanceModal";
import { DEFAULT_GATE_ADVANCE_TEMPLATE_CATEGORY } from "../isf/isfStudyGateService";
import { cloneCohortScheduleToStudy } from "../schedule/scheduleService";
import {
  STUDY_MGMT_TRUNCATE_LEN,
  STUDY_MGMT_FILTERS_INITIAL,
  STUDY_MGMT_COLUMN_DATE_PRESETS,
  applyStudyMgmtFilters,
  buildGateFilterOptions,
  buildGroupTypeFilterOptions,
  buildPiFilterOptions,
  buildStatusFilterOptions,
  buildStudyEnrichmentMapsFromRows,
  getStudyGateCode,
  getStudyPiName,
  getStudyRowId,
  getStudyScheduleId,
  getStudySchedulePublishLabel,
  getStudyMgmtAvatarColor,
  getStudyStatusLabel,
  isMultipleGroupType,
  SCHEDULE_STATUS_DRAFT,
  SCHEDULE_STATUS_NONE,
  SCHEDULE_STATUS_PUBLISHED,
  studyHasScheduleSetup,
} from "./studyMgmtReportUtils";
import { fetchStudyMgmtListBundle } from "./studyMgmtListService";
import ScheduleStartChoiceModal, {
  buildCloneableStudies,
} from "./ScheduleStartChoiceModal";
import ScheduleWorkspaceModal from "./ScheduleWorkspaceModal";
import StudyArchiveWizard from "./StudyArchiveWizard";
import StudyUsersEditModal from "./StudyUsersEditModal";
import { isStudyAlreadyArchived } from "./studyArchiveService";
import {
  ARCHIVED_STUDY_READONLY_MESSAGE,
  isArchivedStudyReadOnly,
} from "./studyArchivedReadOnly";
import { loadStudyForModal } from "./studyFormLoadService";

const parseMappedSiteIds = (raw) =>
  String(raw ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

const staffRawToDualList = (staffList) =>
  staffToDropdownOptions(staffList).map((opt) => ({
    ...opt,
    userName:
      opt.staff?.StaffName ||
      `${opt.staff?.FirstName || ""} ${opt.staff?.LastName || ""}`.trim(),
    emailId: opt.staff?.EmailId || "",
  }));

const STUDY_MGMT_ANCHOR_COLUMN = "Study";
const STUDY_MGMT_PI_COLUMN = "PI";
const STUDY_MGMT_GATE_COLUMN = "Gate";
const STUDY_MGMT_SITES_COLUMN = "Sites";
const STUDY_MGMT_USERS_COLUMN = "Users";

const STUDY_MGMT_FIELD_OPTIONS = [
  { key: STUDY_MGMT_ANCHOR_COLUMN, label: "Study" },
  { key: "GroupType", label: "Group Type" },
  { key: STUDY_MGMT_PI_COLUMN, label: "PI" },
  { key: "SystemDateTime", label: "Creation Date & Time" },
  { key: "StudyStatus", label: "Status" },
  { key: STUDY_MGMT_GATE_COLUMN, label: "Gate" },
  { key: STUDY_MGMT_USERS_COLUMN, label: "Users" },
  { key: STUDY_MGMT_SITES_COLUMN, label: "Sites" },
];

const DEFAULT_STUDY_MGMT_COLUMNS = [
  STUDY_MGMT_ANCHOR_COLUMN,
  "GroupType",
  STUDY_MGMT_PI_COLUMN,
  "SystemDateTime",
  "StudyStatus",
  STUDY_MGMT_GATE_COLUMN,
  STUDY_MGMT_USERS_COLUMN,
  STUDY_MGMT_SITES_COLUMN,
];

const STUDY_MGMT_LOCKED_COLUMNS = [STUDY_MGMT_ANCHOR_COLUMN];
const STUDY_MGMT_VISIBLE_AVATARS = 3;

const idToUpperCase = (id) => String(id || "").trim().toUpperCase();

const formatStudyNumberLabel = (row) => {
  const raw = String(row.StudyNumber ?? row.StudyCode ?? "").trim();
  return raw || "—";
};

const renderTruncatedHoverText = (value, maxLen = STUDY_MGMT_TRUNCATE_LEN) => {
  const text = String(value ?? "").trim() || "—";
  if (text === "—" || text.length <= maxLen) return text;
  return (
    <HoverPreview content={text} previewLength={maxLen} appendEllipsis className="study-mgmt-report__truncated-text" />
  );
};

const truncateStudyLabel = (value, maxLen = STUDY_MGMT_TRUNCATE_LEN) => {
  const text = String(value ?? "").trim() || "—";
  if (text === "—" || text.length <= maxLen) {
    return { label: text, isTruncated: false, full: text };
  }
  return {
    label: `${text.slice(0, maxLen)}…`,
    isTruncated: true,
    full: text,
  };
};

const renderStudyCell = (row, { onOpenStudy } = {}) => {
  const color = row.StudySchColor || row.StudyColor || row.studySchColor;
  const studyNumber = formatStudyNumberLabel(row);
  const studyName = String(row.StudyName ?? "").trim() || "—";
  const { label, isTruncated, full } = truncateStudyLabel(studyName);

  return (
    <span className="study-mgmt-report__study-cell">
      <button
        type="button"
        className="report-table__cell-link study-mgmt-report__study-link"
        onClick={(event) => {
          event.stopPropagation();
          onOpenStudy?.(row);
        }}
        title={isTruncated ? full : `View ${full}`}
        aria-label={`View ${full}`}
      >
        <span className="study-mgmt-report__study-link-label">{label}</span>
      </button>
      <span
        className="study-mgmt-report__number-badge"
        style={color ? { "--study-color": color } : undefined}
        title={studyNumber}
      >
        <span
          className="study-mgmt-report__color-dot"
          aria-hidden="true"
        />
        <span className="study-mgmt-report__number-badge-text">{studyNumber}</span>
      </span>
    </span>
  );
};

const renderScheduleStatusBadge = (label, loading = false) => {
  if (loading) {
    return <span className="study-mgmt-report__schedule-badge is-loading">…</span>;
  }
  if (!label || label === SCHEDULE_STATUS_NONE) {
    return (
      <span className="study-mgmt-report__schedule-badge is-none" title={label}>
        {label}
      </span>
    );
  }
  const tone =
    label === SCHEDULE_STATUS_PUBLISHED
      ? "published"
      : label === SCHEDULE_STATUS_DRAFT
        ? "draft"
        : "none";
  return (
    <span className={`study-mgmt-report__schedule-badge is-${tone}`} title={label}>
      {label}
    </span>
  );
};

const STUDY_STATUS_STYLES = {
  Live: { background: "#d1fae5", color: "#065f46" },
  Archived: { background: "#f3f4f6", color: "#374151" },
};

const STUDY_STATUS_OPTIONS = [
  { value: "Live", label: "Live" },
  { value: "Archived", label: "Archived" },
];

function placeStudyStatusMenu(anchorEl, { width = 180, height = 160 } = {}) {
  const rect = anchorEl?.getBoundingClientRect?.();
  if (!rect || (!rect.width && !rect.height)) return { top: 8, left: 8 };
  const gap = 8;
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const menuW = Math.max(160, Number(width) || 180);
  const menuH = Math.max(120, Number(height) || 160);
  let left = rect.left;
  let top = rect.bottom + gap;
  if (top + menuH > vh - pad) {
    const above = rect.top - menuH - gap;
    top = above >= pad ? above : Math.max(pad, vh - pad - menuH);
  }
  if (left + menuW > vw - pad) left = vw - pad - menuW;
  if (left < pad) left = pad;
  return { top, left };
}

function StudyStatusDropdown({ value, disabled, disabledReason, onChange }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const style = STUDY_STATUS_STYLES[value] || STUDY_STATUS_STYLES.Live;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = (event) => {
      const menu = menuRef.current;
      const target = event?.target;
      if (menu && target && (menu === target || menu.contains(target))) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  if (disabled) {
    return (
      <span title={disabledReason || value || "Status cannot be changed"}>
        <span className="eplanner-status-pill" style={style}>
          {value}
        </span>
      </span>
    );
  }

  return (
    <div className="eplanner-appt-table__status" ref={wrapRef}>
      <button
        type="button"
        className="eplanner-appt-table__status-pill"
        style={style}
        onClick={(event) => {
          event.stopPropagation();
          setMenuPos(placeStudyStatusMenu(wrapRef.current, { width: 180, height: 220 }));
          setOpen((current) => !current);
        }}
        title={value ? `${value} — Change status` : "Change status"}
      >
        <span>{value}</span>
        <i className="fas fa-pencil-alt" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="eplanner-appt-table__status-menu"
              role="listbox"
              aria-label="Change study status"
              style={{ top: menuPos.top, left: menuPos.left }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="eplanner-appt-table__status-menu-title">
                Change status
              </div>
              {STUDY_STATUS_OPTIONS.map((option) => {
                const selected = option.value === value;
                const optionStyle =
                  STUDY_STATUS_STYLES[option.value] || STUDY_STATUS_STYLES.Live;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={[
                      "eplanner-appt-table__status-menu-item",
                      selected ? "is-selected" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      setOpen(false);
                      if (!selected) onChange?.(option.value);
                    }}
                  >
                    <span
                      className="eplanner-appt-table__status-dot"
                      style={{
                        background: optionStyle.background || "#cbd5e1",
                      }}
                      aria-hidden="true"
                    />
                    <span>{option.label}</span>
                    {selected ? (
                      <i className="fas fa-check" aria-hidden="true" />
                    ) : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

const renderStudyGroupTypeCell = (
  row,
  studyEnrichment = {},
  { onOpenSchedule, canAddEditSchedule = false } = {},
) => {
  const studyId = getStudyRowId(row);
  const hasSchedule = studyHasScheduleSetup(row);
  const archived = isArchivedStudyReadOnly(row);

  if (!hasSchedule) {
    if (!canAddEditSchedule || archived) {
      return "—";
    }
    return (
      <span className="study-mgmt-report__group-cell">
        <button
          type="button"
          className="report-table__cell-link study-mgmt-report__group-type-link"
          onClick={(event) => {
            event.stopPropagation();
            onOpenSchedule?.(row);
          }}
          title="Add schedule"
        >
          Add schedule
        </button>
      </span>
    );
  }

  const scheduleLabel = getStudySchedulePublishLabel(
    row,
    studyEnrichment.scheduleStatusByStudyId,
  );
  const scheduleLoading = Boolean(studyEnrichment.pendingStudyIds?.[studyId]);
  const countRaw = row.TotalGroup;
  const count =
    countRaw != null && String(countRaw).trim() !== "" && String(countRaw).trim() !== "0"
      ? String(countRaw).trim()
      : "";
  const label = String(row.GroupType ?? "").trim() || "—";
  const groupName = String(row.GroupName ?? row.groupName ?? "").trim();
  const baseLabel = groupName || label;
  const isMultiple = isMultipleGroupType(row);
  const displayLabel =
    isMultiple && count ? `${baseLabel} (${count})` : baseLabel;

  return (
    <span className="study-mgmt-report__group-cell">
      <button
        type="button"
        className="report-table__cell-link study-mgmt-report__group-type-link"
        onClick={(event) => {
          event.stopPropagation();
          onOpenSchedule?.(row);
        }}
        title="Open schedule readiness"
      >
        {displayLabel}
      </button>
      {renderScheduleStatusBadge(scheduleLabel, scheduleLoading)}
    </span>
  );
};

const StudyUsersAvatarCell = ({ users = [], loading = false }) => {
  const anchorRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [cardPos, setCardPos] = useState(null);

  useEffect(
    () => () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  if (loading) {
    return <span className="study-mgmt-report__users-loading">…</span>;
  }
  if (!users.length) return "—";

  const visible = users.slice(0, STUDY_MGMT_VISIBLE_AVATARS);
  const overflow = users.length - visible.length;
  const names = users.map((user) => user.name).filter(Boolean);

  const openCard = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setCardPos({
      top: Math.min(rect.bottom + 8, window.innerHeight - 12),
      left: Math.min(rect.left, window.innerWidth - 220),
    });
  };

  const scheduleClose = () => {
    closeTimerRef.current = window.setTimeout(() => setCardPos(null), 160);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  return (
    <>
      <span
        ref={anchorRef}
        className="study-mgmt-report__users-stack"
        onMouseEnter={openCard}
        onMouseLeave={scheduleClose}
        onFocus={openCard}
        onBlur={scheduleClose}
      >
        {visible.map((user) => (
          <span
            key={user.staffId || user.name}
            className="study-mgmt-report__user-avatar"
            style={{ backgroundColor: getStudyMgmtAvatarColor(user.name) }}
            title={user.name}
          >
            {getInitials(user.name)}
          </span>
        ))}
        {overflow > 0 ? (
          <span className="study-mgmt-report__user-avatar study-mgmt-report__user-avatar--more">
            +{overflow}
          </span>
        ) : null}
      </span>
      {cardPos
        ? createPortal(
            <div
              className="study-mgmt-report__users-hover-card"
              style={{ top: `${cardPos.top}px`, left: `${cardPos.left}px` }}
              role="tooltip"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              {names.map((name) => (
                <div key={name} className="study-mgmt-report__users-hover-item">
                  <span
                    className="study-mgmt-report__user-avatar"
                    style={{ backgroundColor: getStudyMgmtAvatarColor(name) }}
                    aria-hidden="true"
                  >
                    {getInitials(name)}
                  </span>
                  <span>{name}</span>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

const StudySitesCell = ({ sites = [], loading = false }) => {
  const anchorRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [cardPos, setCardPos] = useState(null);

  useEffect(
    () => () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  if (loading) {
    return <span className="study-mgmt-report__sites-loading">…</span>;
  }
  if (!sites.length) return "—";

  if (sites.length === 1) {
    const site = sites[0];
    return renderTruncatedHoverText(site.name);
  }

  const openCard = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setCardPos({
      top: Math.min(rect.bottom + 8, window.innerHeight - 12),
      left: Math.min(rect.left, window.innerWidth - 220),
    });
  };

  const scheduleClose = () => {
    closeTimerRef.current = window.setTimeout(() => setCardPos(null), 160);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  return (
    <>
      <span
        ref={anchorRef}
        className="study-mgmt-report__sites-count"
        onMouseEnter={openCard}
        onMouseLeave={scheduleClose}
        onFocus={openCard}
        onBlur={scheduleClose}
        title={`${sites.length} sites`}
        aria-label={`${sites.length} sites — hover to view`}
      >
        {sites.length}
      </span>
      {cardPos
        ? createPortal(
            <div
              className="study-mgmt-report__sites-hover-card"
              style={{ top: `${cardPos.top}px`, left: `${cardPos.left}px` }}
              role="tooltip"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              {sites.map((site) => (
                <div key={site.siteId || site.name} className="study-mgmt-report__sites-hover-item">
                  <span
                    className="study-mgmt-report__site-chip"
                    style={{ backgroundColor: getStudyMgmtAvatarColor(site.name) }}
                    aria-hidden="true"
                  >
                    {site.abbr}
                  </span>
                  <span>{site.name}</span>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

const StudyManagement = () => {

  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  /** StaffIds assigned when edit modal opened — used to build RemovedUserList (legacy dual-list parity). */
  const initialAssignedStaffIdsRef = useRef([]);
  const [studies, setStudies] = useState([]);
  const [studiesLoading, setStudiesLoading] = useState(true);
  const [gateAdvanceStudy, setGateAdvanceStudy] = useState(null);
  const [tableFilters, setTableFilters] = useState(STUDY_MGMT_FILTERS_INITIAL);
  const [studyEnrichment, setStudyEnrichment] = useState({
    gateByStudyId: {},
    usersByStudyId: {},
    sitesByStudyId: {},
    scheduleStatusByStudyId: {},
    pendingStudyIds: {},
    enriching: false,
  });
  const studiesLoadedRef = useRef(false);
  const studiesCountRef = useRef(0);
  const staffByIdRef = useRef(new Map());
  const studiesFetchSeqRef = useRef(0);
  const reloadStudiesRef = useRef(async () => {});

  const applyListEnrichment = useCallback((rows) => {
    const maps = buildStudyEnrichmentMapsFromRows(rows);
    setStudyEnrichment({
      ...maps,
      pendingStudyIds: {},
      enriching: false,
    });
  }, []);

  const mergeStudyListRows = useCallback((liveRows, archivedRows) => {
    const byId = new Map();
    liveRows.forEach((row) => {
      const id = getStudyRowId(row);
      if (id) byId.set(id.toLowerCase(), row);
    });
    archivedRows.forEach((row) => {
      const id = getStudyRowId(row);
      if (id) byId.set(id.toLowerCase(), row);
    });
    return Array.from(byId.values());
  }, []);

  const reloadStudies = useCallback(async (options = {}) => {
    if (!userData?.OrganizationId || !userData?.StaffId) {
      setStudiesLoading(false);
      return;
    }

    const fetchSeq = ++studiesFetchSeqRef.current;
    const silent = Boolean(options.silent) && studiesCountRef.current > 0;

    try {
      if (!silent) setStudiesLoading(true);
      const { liveRows, archivedRows } = await fetchStudyMgmtListBundle(userData);
      if (fetchSeq !== studiesFetchSeqRef.current) return;

      const archivedNormalized = archivedRows.map((row) => ({
        ...row,
        StudyStatus: String(row?.StudyStatus || "").trim() || "Archived",
        InActive: row?.InActive ?? 1,
        Archive: row?.Archive ?? 1,
      }));

      const rows = mergeStudyListRows(liveRows, archivedNormalized);
      setStudies(rows);
      studiesCountRef.current = rows.length;
      studiesLoadedRef.current = true;
      applyListEnrichment(rows);
    } catch {
      if (fetchSeq === studiesFetchSeqRef.current) {
        setStudies([]);
        studiesCountRef.current = 0;
        setStudyEnrichment({
          gateByStudyId: {},
          usersByStudyId: {},
          sitesByStudyId: {},
          scheduleStatusByStudyId: {},
          pendingStudyIds: {},
          enriching: false,
        });
      }
    } finally {
      if (fetchSeq === studiesFetchSeqRef.current) {
        setStudiesLoading(false);
      }
    }
  }, [applyListEnrichment, mergeStudyListRows, userData]);

  reloadStudiesRef.current = reloadStudies;

  const location = useLocation();

  useEffect(() => {
    if (!userData) return;
    reloadStudiesRef.current();
  }, [userData, location.key]);

  const handleRefresh = () => {
    studiesLoadedRef.current = false;
    reloadStudiesRef.current();
  };

  const [showCapaCreateModal, setShowCapaCreateModal] = useState(false);
  const [capaCreateContext, setCapaCreateContext] = useState(null);
  const [scheduleStartStudy, setScheduleStartStudy] = useState(null);
  const [scheduleWorkspace, setScheduleWorkspace] = useState(null);
  const [scheduleCloneLoading, setScheduleCloneLoading] = useState(false);
  const [scheduleCloneError, setScheduleCloneError] = useState("");
  const [archiveStudy, setArchiveStudy] = useState(null);
  const [usersEditStudy, setUsersEditStudy] = useState(null);

  const [department, setDepartments] = useState([]);
  const [studyColors, setStudyColors] = useState(() => utils.studyColors());
  const [siteOptions, setSiteOptions] = useState([]);
  const [allStaffRaw, setAllStaffRaw] = useState([]);
  const [studyStatus] = useState([
    {
      value: "Live",
      label: "Live",
    },
  ]);
  const [isStudyPopupShow, setStudyPopupShow] = useState(false);
  const [studyModalMode, setStudyModalMode] = useState("add");
  const [modalStudyArchived, setModalStudyArchived] = useState(false);
  const studyStatusDropdownOptions = useMemo(() => {
    if (!modalStudyArchived) return studyStatus;
    return [{ value: "Archived", label: "Archived" }];
  }, [modalStudyArchived, studyStatus]);
  const isStudyModalReadOnly =
    studyModalMode === "view" || modalStudyArchived;

  useEffect(() => {
    if (!isStudyPopupShow) return undefined;
    const root = document.documentElement;
    const prevHtmlOverflow = root.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    root.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.classList.add("modal-open");
    return () => {
      root.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.classList.remove("modal-open");
    };
  }, [isStudyPopupShow]);

  /** Selected staff rows for DualListBox (value = StaffId) */
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [formData, setFormData] = useState({
    studyId: "",
    studyTitle: "",
    studyColor: null,
    prefix: "STD",
    studyNumber: "00000",
    principalInvestigator: null,
    eudractNumber: "",
    rdNumber: "",
    department: "",
    siteId: "",
    selectedSites: [],
    status: studyStatus.find((r) => r.value.toLowerCase() === "live") || null,
    sponsorName: "",
    irasNumber: "",
    cordinatorName: "",
    cordinatorEmail: "",
    cordinatorContact: "",
    ...EMPTY_STUDY_METADATA,
  });

  const getStudyBasicValidationErrors = () => {
    const errs = [];
    if (!String(formData.studyNumber ?? "").trim()) {
      errs.push("Study number is required.");
    }
    if (!String(formData.studyTitle ?? "").trim()) {
      errs.push("Study title is required.");
    }
    const colorVal =
      formData.studyColor &&
      (typeof formData.studyColor === "object"
        ? formData.studyColor.value
        : formData.studyColor);
    if (colorVal === undefined || colorVal === null || colorVal === "") {
      errs.push("Study color is required.");
    }
    const piId = formData.principalInvestigator?.value;
    if (piId === undefined || piId === null || piId === "") {
      errs.push("Principal Investigator is required.");
    }
    if (!(formData.selectedSites || []).length) {
      errs.push("At least one site is required.");
    }
    if (
      (formData.selectedSites || []).length > 0 &&
      !siteFilteredStaff.length
    ) {
      errs.push("No staff are mapped to the selected site(s).");
    }
    if (!assignedStaff.length && siteFilteredStaff.length) {
      errs.push("Assign at least one staff member.");
    }
    if (
      piId !== undefined &&
      piId !== null &&
      piId !== "" &&
      !assignedStaff.some((s) => String(s.value) === String(piId))
    ) {
      errs.push(
        "Principal Investigator must be included in the Assign Staff list.",
      );
    }
    const piStaff = allStaffRaw.find(
      (staff) => String(staff.StaffId) === String(piId),
    );
    if (piStaff && !staffMappedToAnySite(piStaff, selectedSiteIds)) {
      errs.push(
        "Principal Investigator must be mapped to a selected study site.",
      );
    }
    const invalidAssigned = assignedStaff.filter((row) => {
      const staff = allStaffRaw.find(
        (item) =>
          String(item.StaffId).toLowerCase() === String(row.value).toLowerCase(),
      );
      return staff && !staffMappedToAnySite(staff, selectedSiteIds);
    });
    if (invalidAssigned.length) {
      errs.push(
        "One or more assigned staff are not mapped to the selected study site(s).",
      );
    }
    errs.push(
      ...getStudyMetadataValidationErrors(formData, {
        isCreating: !formData.studyId,
      }),
    );
    return errs;
  };

  const handleStudyMetadataChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFormInput = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    });
  };

  const handleClose = () => {
    setStudyPopupShow(false);
    setStudyModalMode("add");
    setModalStudyArchived(false);
    setAssignedStaff([]);
    initialAssignedStaffIdsRef.current = [];
  };

  const resetForm = () => {
    setFormData({
      StaffId: "",
      firstName: "",
      lastName: "",
      contactNumber: "",
      profilePicture: "",
      prefix: "STD",
      employeeNumber: "00000",
      email: "",
      confirmEmail: "",
      dob: new Date(),
      startDate: new Date(),
      accessDate: new Date(),
      gender: "",
      role: "",
      department: "",
      lineManager: "",
      haveLineManagerRights: false,
      addressLine1: "",
      postalCode: "",
      city: "",
      county: "",
      country: "",
      studies: [],
    });
  };

  const getCommonStudyData = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return null;

    try {
      const [response, sites, staffRaw] = await Promise.all([
        api.post(API_ENDPOINTS.USER.GET_USER_PROFILE_COMMONDATA, {
          OrganizationId: userData.OrganizationId,
          StaffIDRef: userData.StaffId,
          CreatedBy: userData.StaffId,
          PageName: "AddStudyReact",
        }),
        getActiveSiteLookup(userData, {}, { forceRefresh: true }),
        getInternalActiveStaffLookup(userData, {}, { forceRefresh: true }),
      ]);

      if (response.success === 1) {
        const departmentsData = utils.mapToOptions(
          response.data.DepartmentList,
          "DepartmentName",
          "DepartmentId",
        );
        const siteOpts = siteToDropdownOptions(sites);

        setDepartments(departmentsData);
        setSiteOptions(siteOpts);
        setAllStaffRaw(staffRaw);

        const study = response.data.UniqueCode.filter(
          (item) => item.Perpose === "Study",
        )[0];
        setFormData((prev) => ({
          ...prev,
          prefix: study?.Prefix || "STD",
          studyNumber:
            study?.NumberFormate?.toString().padStart(5, "0") || "00001",
        }));

        return {
          departments: departmentsData,
          siteOptions: siteOpts,
          staffRaw,
          prefix: study?.Prefix || "STD",
          studyNumber:
            study?.NumberFormate?.toString().padStart(5, "0") || "00001",
        };
      }
    } catch (error) {
      return null;
    }
  };

  const openStudyModal = useCallback(
    async (row, mode = "add") => {
      try {
        const archived = !!(row && isArchivedStudyReadOnly(row));
        setModalStudyArchived(archived);
        setStudyModalMode(archived ? "view" : mode);
        showLoader(mode === "add" ? "Preparing form..." : "Loading study...");
        const commonData = await getCommonStudyData();

        if (!row || mode === "add") {
          setModalStudyArchived(false);
          initialAssignedStaffIdsRef.current = [];
          setAssignedStaff([]);
          setFormData((prev) => ({
            ...prev,
            studyId: "",
            studyTitle: "",
            studyColor: null,
            principalInvestigator: null,
            siteId: "",
            selectedSites: [],
            eudractNumber: "",
            rdNumber: "",
            department: "",
            sponsorName: "",
            irasNumber: "",
            cordinatorName: "",
            cordinatorEmail: "",
            cordinatorContact: "",
            status: studyStatus.find((r) => r.value.toLowerCase() === "live") || null,
            ...EMPTY_STUDY_METADATA,
            prefix: commonData?.prefix || prev.prefix || "STD",
            studyNumber: commonData?.studyNumber || prev.studyNumber || "00001",
          }));
          setStudyPopupShow(true);
          return;
        }

        const loaded = await loadStudyForModal({
          userData,
          row,
          departmentOptions: commonData?.departments || department,
          studyStatusOptions: studyStatus,
          studyColorOptions: studyColors,
          siteOptions: commonData?.siteOptions || siteOptions,
          staffRaw: commonData?.staffRaw || allStaffRaw,
          prefix: commonData?.prefix || formData.prefix || "STD",
        });

        initialAssignedStaffIdsRef.current = loaded.initialAssignedStaffIds;
        setAssignedStaff(loaded.assignedStaff);
        setFormData((prev) => ({
          ...prev,
          ...loaded.formData,
        }));
        setStudyPopupShow(true);
      } catch (error) {
        console.error("Error opening study modal:", error);
        showAlert({
          title: "Study",
          message: "Could not load study details.",
          type: "danger",
        });
      } finally {
        hideLoader();
      }
    },
    [
      allStaffRaw,
      department,
      formData.prefix,
      hideLoader,
      showAlert,
      showLoader,
      siteOptions,
      studyColors,
      studyStatus,
      userData,
    ],
  );

  const handleEdit = async (row) => {
    await openStudyModal(row, row ? "edit" : "add");
  };

  const departmentSelected = useMemo(() => {
    const value = formData.department;
    if (!value) return null;
    if (typeof value === "object" && value.value != null) return value;
    return (
      department.find((opt) => String(opt.value) === String(value)) || null
    );
  }, [department, formData.department]);

  const studyModalTitle =
    modalStudyArchived || studyModalMode === "view"
      ? "View Study"
      : studyModalMode === "add"
        ? "Add Study"
        : "Edit Study";

  const handleStudyModalEnableEdit = useCallback(() => {
    if (modalStudyArchived) {
      showAlert({
        title: "Archived study",
        message: ARCHIVED_STUDY_READONLY_MESSAGE,
        type: "info",
      });
      return;
    }
    setStudyModalMode("edit");
  }, [modalStudyArchived, showAlert]);

  const selectedSiteIds = useMemo(
    () => (formData.selectedSites || []).map((s) => s.value).filter(Boolean),
    [formData.selectedSites],
  );

  const siteFilteredStaff = useMemo(() => {
    if (!selectedSiteIds.length) return [];
    return allStaffRaw.filter((staff) =>
      staffMappedToAnySite(staff, selectedSiteIds),
    );
  }, [allStaffRaw, selectedSiteIds]);

  const staffList = useMemo(() => {
    const byId = new Map();
    siteFilteredStaff.forEach((staff) => {
      byId.set(String(staff.StaffId).toLowerCase(), staff);
    });
    assignedStaff.forEach((row) => {
      const match = allStaffRaw.find(
        (staff) =>
          String(staff.StaffId).toLowerCase() === String(row.value).toLowerCase(),
      );
      if (match) byId.set(String(match.StaffId).toLowerCase(), match);
    });
    return staffRawToDualList([...byId.values()]);
  }, [siteFilteredStaff, allStaffRaw, assignedStaff]);

  const principalInvestigators = useMemo(
    () =>
      staffRawToDualList(
        siteFilteredStaff.filter(
          (staff) =>
            String(staff?.RoleName || staff?.Roles || "")
              .trim()
              .toLowerCase() === "principal investigator",
        ),
      ),
    [siteFilteredStaff],
  );
  const handleDepartment = (department) => {
    setFormData((prev) => ({
      ...prev,
      department: department?.value || "",
    }));
  };
  const handleStudyColors = (studyColorOption) => {
    setFormData((prev) => ({
      ...prev,
      studyColor: studyColorOption || null,
    }));
  };
  const handlePI = (piOption) => {
    const opt = piOption || null;
    setFormData((prev) => ({
      ...prev,
      principalInvestigator: opt,
    }));
    if (opt?.value != null) {
      const match = staffList.find(
        (s) => String(s.value) === String(opt.value),
      );
      if (match) {
        setAssignedStaff((prev) => {
          if (prev.some((s) => String(s.value) === String(opt.value))) {
            return prev;
          }
          return [...prev, match];
        });
      }
    }
  };
  const handleStatus = (statusOption) => {
    setFormData((prev) => ({
      ...prev,
      status: statusOption || null,
    }));
  };

  const handleSites = (selected) => {
    setFormData((prev) => ({
      ...prev,
      selectedSites: Array.isArray(selected) ? selected : [],
    }));
  };


  const buildStudyActivityPayload = () => {
    const idToUpperCase = (v) =>
      v === undefined || v === null || v === ""
        ? ""
        : String(v).toUpperCase();

    /** API expects numeric study number only (no prefix / letters). */
    const studyNumDigitsOnly = String(formData.studyNumber ?? "")
      .replace(/\D/g, "")
      .trim();
    const colorHex =
      formData.studyColor &&
      (typeof formData.studyColor === "object"
        ? formData.studyColor.value
        : formData.studyColor);

    const staffRowToUser = (s, isAssigned) => ({
      UserId: idToUpperCase(s.value),
      UserName: (s.userName ?? "").trim(),
      EmailId: (s.emailId ?? "").trim(),
      IsAssigned: !!isAssigned,
    });

    /** Selected list → UserList (legacy bootstrap-duallistbox selected options). */
    const userList = assignedStaff.map((s) => staffRowToUser(s, true));

    /**
     * Non-selected but previously assigned → RemovedUserList (only when those ids
     * were on the study when edit opened). Insert: ref is empty → [].
     */
    const currentIdSet = new Set(
      assignedStaff.map((s) => String(s.value).toLowerCase()),
    );
    const removedUserList = [];
    initialAssignedStaffIdsRef.current.forEach((rawId) => {
      const id = String(rawId).toLowerCase();
      if (currentIdSet.has(id)) return;
      const staff = staffList.find(
        (s) => String(s.value).toLowerCase() === id,
      );
      if (staff) {
        removedUserList.push(staffRowToUser(staff, false));
      }
    });

    const selectedSites = formData.selectedSites || [];
    const primarySiteId = selectedSites[0]?.value || "";

    return {
      OrganizationId: idToUpperCase(userData.OrganizationId),
      CreatedBy: idToUpperCase(userData.StaffId),
      StudyId: formData.studyId || "",
      Action: formData.studyId ? "Update" : "Insert",
      Study: (formData.studyTitle || "").trim(),
      StudyNum: studyNumDigitsOnly,
      PI: idToUpperCase(formData.principalInvestigator?.value),
      EudractNumber: (formData.eudractNumber || "").trim(),
      Randnumber: (formData.rdNumber || "").trim(),
      SiteID: String(primarySiteId).trim(),
      SiteList: selectedSites.map((site) => ({
        SiteId: idToUpperCase(site.value),
        SiteName: site.label || site.site?.SiteName || "",
      })),
      Department: formData.department ?? "",
      StudyColor: colorHex ?? "",
      Status: formData.status?.value ?? "",
      UserList: userList,
      RemovedUserList: removedUserList,
      StudyLocationList: [],
      ProjectId: "",
      SponsorName: (formData.sponsorName || "").trim(),
      IRASNumber: (formData.irasNumber || "").trim(),
      CordinatorName: (formData.cordinatorName || "").trim(),
      CordinatorPhone: (formData.cordinatorContact || "").trim(),
      CordinatorEmail: (formData.cordinatorEmail || "").trim(),
      ...buildStudyMetadataPayload(formData),
    };
  };

  const handleUserSaveUpdate = async () => {
    if (modalStudyArchived || isStudyModalReadOnly) {
      showAlert({
        title: "Archived study",
        message: ARCHIVED_STUDY_READONLY_MESSAGE,
        type: "info",
      });
      return;
    }

    const basicErrs = getStudyBasicValidationErrors();
    if (basicErrs.length > 0) {
      showAlert({
        title: "Study details",
        message: basicErrs.join("\n"),
        type: "warning",
      });
      return;
    }

    const statusValue = String(formData.status?.value || "").trim().toLowerCase();
    const wantsArchive =
      statusValue === "archive" || statusValue === "archived";

    if (wantsArchive) {
      if (!formData.studyId) {
        showAlert({
          title: "Archive study",
          message: "Save the study as Live first, then archive it.",
          type: "warning",
        });
        return;
      }
      const row =
        studies.find(
          (item) =>
            String(getStudyRowId(item)).toLowerCase() ===
            String(formData.studyId).toLowerCase(),
        ) || {
          StudyId: formData.studyId,
          StudyName: formData.studyTitle,
          StudyNumber: formData.studyNumber,
          StudyStatus: formData.status?.value,
          StudyColor:
            typeof formData.studyColor === "object"
              ? formData.studyColor?.value
              : formData.studyColor,
        };
      setArchiveStudy(row);
      return;
    }

    try {
      showLoader("Saving study...");
      const payload = buildStudyActivityPayload();
      const response = await api.post(
        API_ENDPOINTS.STUDY.STUDY_ACTIVITY,
        payload,
      );
      if (utils.isApiSuccess(response)) {
        showAlert({
          title: "Study",
          message:
            utils.getApiResponseMessage(response, "Study saved successfully."),
          type: "success",
        });
        handleClose();
        reloadStudiesRef.current();
      } else {
        showAlert({
          title: "Study",
          message: utils.getApiResponseMessage(
            response,
            "Unable to save the study. Please check your data and try again.",
          ),
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Study",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong while saving the study. Please try again.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleToggleStudyStaff = useCallback((staffRow, shouldAdd) => {
    const staffId = staffRow?.StaffId;
    if (!staffId) return;

    if (shouldAdd) {
      const option = staffRawToDualList([staffRow])[0];
      setAssignedStaff((prev) => {
        if (
          prev.some(
            (item) =>
              String(item.value).toLowerCase() === String(staffId).toLowerCase(),
          )
        ) {
          return prev;
        }
        return [...prev, option];
      });
      return;
    }

    setAssignedStaff((prev) =>
      prev.filter(
        (item) =>
          String(item.value).toLowerCase() !== String(staffId).toLowerCase(),
      ),
    );
    setFormData((prev) => {
      if (
        String(prev.principalInvestigator?.value).toLowerCase() ===
        String(staffId).toLowerCase()
      ) {
        return { ...prev, principalInvestigator: null };
      }
      return prev;
    });
  }, []);

  const assignedStaffIds = useMemo(
    () => assignedStaff.map((item) => item.value).filter(Boolean),
    [assignedStaff],
  );

  /** Auto-assign all staff mapped to selected site(s). */
  useEffect(() => {
    if (!selectedSiteIds.length) {
      setFormData((prev) =>
        prev.principalInvestigator
          ? { ...prev, principalInvestigator: null }
          : prev,
      );
      setAssignedStaff([]);
      return;
    }

    setAssignedStaff(staffRawToDualList(siteFilteredStaff));

    setFormData((prev) => {
      const piId = prev.principalInvestigator?.value;
      if (
        piId &&
        !siteFilteredStaff.some(
          (staff) => String(staff.StaffId) === String(piId),
        )
      ) {
        return { ...prev, principalInvestigator: null };
      }
      return prev;
    });
  }, [selectedSiteIds, siteFilteredStaff]);

  /** Ensure PI appears in Assign Staff once staff list is loaded (and if user re-adds PI after list refresh). */
  useEffect(() => {
    const pid = formData.principalInvestigator?.value;
    if (pid == null || pid === "") return;
    if (!staffList.length) return;
    const match = staffList.find(
      (s) => String(s.value) === String(pid),
    );
    if (!match) return;
    setAssignedStaff((prev) => {
      if (prev.some((s) => String(s.value) === String(pid))) return prev;
      return [...prev, match];
    });
  }, [staffList, formData.principalInvestigator]);

  const isSystemAdmin = userData?.Role === "System Administrator";
  const userPermissions = isSystemAdmin
    ? getAllPermissionKeys()
    : parsePermissions(userData?.Permission || "");
  const canViewStudyList =
    isSystemAdmin || hasPermission(userPermissions, "STUDY_LIST");
  const canAddStudy =
    !!userData &&
    (isSystemAdmin || hasPermission(userPermissions, "ADD_STUDY"));
  const canCreateCapa =
    !!userData &&
    (isSystemAdmin || hasPermission(userPermissions, "ADD_CAPA"));
  const canAddEditSchedule =
    !!userData &&
    (isSystemAdmin || hasPermission(userPermissions, "ADD_EDIT_SCHEDULE"));

  const handleOpenGateAdvance = useCallback((row) => {
    const studyId = getStudyRowId(row);
    if (!studyId) return;
    if (isArchivedStudyReadOnly(row)) {
      showAlert({
        title: "Archived study",
        message: ARCHIVED_STUDY_READONLY_MESSAGE,
        type: "info",
      });
      return;
    }
    setGateAdvanceStudy(row);
  }, [showAlert]);

  const handleGateAdvanced = useCallback(() => {
    setGateAdvanceStudy(null);
    reloadStudiesRef.current();
  }, []);

  const gateAdvanceSiteIds = useMemo(() => {
    const studyId = getStudyRowId(gateAdvanceStudy);
    if (!studyId) return [];
    return studyEnrichment.sitesByStudyId[studyId] || [];
  }, [gateAdvanceStudy, studyEnrichment.sitesByStudyId]);

  const handleOpenArchive = useCallback((row) => {
    if (!row) return;
    if (isStudyAlreadyArchived(row)) {
      showAlert({
        title: "Archive study",
        message: "This study is already archived.",
        type: "warning",
      });
      return;
    }
    setArchiveStudy(row);
  }, [showAlert]);

  const handleArchiveCompleted = useCallback(() => {
    setArchiveStudy(null);
    handleClose();
    reloadStudiesRef.current();
  }, []);

  const archiveWizardSiteIds = useMemo(() => {
    const studyId = getStudyRowId(archiveStudy);
    if (!studyId) return [];
    return studyEnrichment.sitesByStudyId[studyId] || [];
  }, [archiveStudy, studyEnrichment.sitesByStudyId]);

  const handleCreateCapa = useCallback((row) => {
    if (row) {
      if (isStudyAlreadyArchived(row)) {
        showAlert({
          title: "Archived study",
          message: ARCHIVED_STUDY_READONLY_MESSAGE,
          type: "info",
        });
        return;
      }
      const studyId = studyRowCapaId(row);
      if (!studyId) return;
      setCapaCreateContext(
        buildCapaContextFromStudy({
          studyId,
          studyLabel: studyRowCapaDisplayLabel(row),
        }),
      );
      setShowCapaCreateModal(true);
      return;
    }
    setCapaCreateContext({
      locked: false,
      module: "study_management",
      entityType: "study",
    });
    setShowCapaCreateModal(true);
  }, [showAlert]);

  const handleOpenUsersEdit = useCallback(
    (row) => {
      if (!row) return;
      if (isArchivedStudyReadOnly(row)) {
        showAlert({
          title: "Archived study",
          message: ARCHIVED_STUDY_READONLY_MESSAGE,
          type: "info",
        });
        return;
      }
      setUsersEditStudy(row);
    },
    [showAlert],
  );

  const handleStatusChange = useCallback(
    (row, nextStatus) => {
      const current = getStudyStatusLabel(row);
      if (nextStatus === current) return;
      if (nextStatus === "Archived") {
        handleOpenArchive(row);
      }
    },
    [handleOpenArchive],
  );

  const handleOpenSchedule = useCallback(
    (row) => {
      if (studyHasScheduleSetup(row)) {
        const id = getStudyScheduleId(row) || getStudyRowId(row);
        if (!id) return;
        setScheduleWorkspace({ scheduleId: id, studyRow: row });
        return;
      }

      if (isStudyAlreadyArchived(row)) {
        showAlert({
          title: "No schedule",
          message:
            "This study has no schedule. A schedule cannot be created because the study is archived.",
          type: "warning",
        });
        return;
      }

      setScheduleCloneError("");
      setScheduleStartStudy(row);
    },
    [showAlert],
  );

  const handleOpenStudyFromCell = useCallback(
    (row) => {
      openStudyModal(row, "view");
    },
    [openStudyModal],
  );

  const handleScheduleStartFromScratch = useCallback(() => {
    const row = scheduleStartStudy;
    const trialStudyId = getStudyRowId(row);
    if (!trialStudyId) return;
    setScheduleStartStudy(null);
    setScheduleCloneError("");
    setScheduleWorkspace({ scheduleId: trialStudyId, studyRow: row });
  }, [scheduleStartStudy]);

  const handleScheduleClone = useCallback(
    async (sourceStudy) => {
      const targetRow = scheduleStartStudy;
      const targetTrialStudyId = getStudyRowId(targetRow);
      const sourceStudyScheduleId = getStudyScheduleId(sourceStudy);
      const organizationId = userData?.OrganizationId;
      const createdBy = userData?.StaffId;

      if (!targetTrialStudyId || !sourceStudyScheduleId || !organizationId || !createdBy) {
        setScheduleCloneError("Missing information required to clone the schedule.");
        return;
      }

      setScheduleCloneLoading(true);
      setScheduleCloneError("");
      showLoader("Cloning schedule...");

      try {
        const result = await cloneCohortScheduleToStudy({
          sourceStudyScheduleId,
          targetTrialStudyId,
          organizationId,
          createdBy,
          studyColor:
            targetRow?.StudyColor ||
            targetRow?.StudySchColor ||
            targetRow?.studyColor ||
            undefined,
        });

        const newStudyScheduleId = String(
          result?.StudyScheduleId ||
            result?.studyScheduleId ||
            targetTrialStudyId ||
            "",
        ).trim();

        const updatedRow = {
          ...targetRow,
          StudySetupStatus: "1",
          StudyScheduleId:
            result?.StudyScheduleId ||
            result?.studyScheduleId ||
            targetRow?.StudyScheduleId,
          IsCohortScheduleLive: 0,
        };

        setScheduleStartStudy(null);
        setScheduleCloneError("");
        await reloadStudiesRef.current();
        setScheduleWorkspace({
          scheduleId: newStudyScheduleId,
          studyRow: updatedRow,
        });
      } catch (err) {
        setScheduleCloneError(err.message || "Could not clone schedule.");
      } finally {
        hideLoader();
        setScheduleCloneLoading(false);
      }
    },
    [hideLoader, scheduleStartStudy, showLoader, userData],
  );

  const cloneableScheduleStudies = useMemo(
    () =>
      buildCloneableStudies(
        studies,
        scheduleStartStudy,
        studyEnrichment.scheduleStatusByStudyId,
      ),
    [scheduleStartStudy, studies, studyEnrichment.scheduleStatusByStudyId],
  );

  const updateTableFilters = useCallback((patch) => {
    setTableFilters((current) => ({ ...current, ...patch }));
  }, []);

  const groupTypeFilterOptions = useMemo(
    () => buildGroupTypeFilterOptions(studies),
    [studies],
  );
  const piFilterOptions = useMemo(() => buildPiFilterOptions(studies), [studies]);
  const statusFilterOptions = useMemo(() => buildStatusFilterOptions(), []);
  const gateFilterOptions = useMemo(() => buildGateFilterOptions(), []);

  const filteredStudies = useMemo(
    () =>
      applyStudyMgmtFilters(studies, tableFilters, {
        gateByStudyId: studyEnrichment.gateByStudyId,
      }),
    [studies, tableFilters, studyEnrichment.gateByStudyId],
  );

  const getStudyMgmtFieldLabel = useCallback(
    (field) =>
      STUDY_MGMT_FIELD_OPTIONS.find((option) => option.key === field)?.label ||
      field,
    [],
  );

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === STUDY_MGMT_ANCHOR_COLUMN) {
        return renderStudyCell(row, { onOpenStudy: handleOpenStudyFromCell });
      }
      if (field === "GroupType") {
        return renderStudyGroupTypeCell(row, studyEnrichment, {
          onOpenSchedule: handleOpenSchedule,
          canAddEditSchedule,
        });
      }
      if (field === STUDY_MGMT_PI_COLUMN) {
        return renderTruncatedHoverText(getStudyPiName(row));
      }
      if (field === "SystemDateTime") {
        return utils.formatDateTimeDisplay(row.SystemDateTime);
      }
      if (field === "StudyStatus") {
        const label = getStudyStatusLabel(row);
        const isArchived = label === "Archived";
        return (
          <StudyStatusDropdown
            value={label}
            disabled={!canAddStudy || isArchived}
            disabledReason={
              isArchived ? "Archived studies cannot change status here." : undefined
            }
            onChange={(nextStatus) => handleStatusChange(row, nextStatus)}
          />
        );
      }
      if (field === STUDY_MGMT_GATE_COLUMN) {
        const studyId = getStudyRowId(row);
        const code = getStudyGateCode(row, studyEnrichment.gateByStudyId);
        return (
          <span className="study-mgmt-report__gate-cell">
            <StudyGateBadge code={code} compact />
            {canAddStudy && studyId && !isArchivedStudyReadOnly(row) ? (
              <button
                type="button"
                className="study-mgmt-report__gate-edit-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenGateAdvance(row);
                }}
                title="Advance study phase (one step at a time; complete pending eISF items first)"
                aria-label="Advance study phase"
              >
                <i className="fas fa-pencil-alt" aria-hidden="true" />
              </button>
            ) : null}
          </span>
        );
      }
      if (field === STUDY_MGMT_USERS_COLUMN) {
        const studyId = getStudyRowId(row);
        return (
          <span className="study-mgmt-report__users-cell">
            <StudyUsersAvatarCell
              users={studyEnrichment.usersByStudyId[studyId] || []}
              loading={Boolean(studyEnrichment.pendingStudyIds?.[studyId])}
            />
            {canAddStudy && studyId && !isArchivedStudyReadOnly(row) ? (
              <button
                type="button"
                className="study-mgmt-report__gate-edit-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenUsersEdit(row);
                }}
                title="Change assigned users"
                aria-label="Change assigned users"
              >
                <i className="fas fa-pencil-alt" aria-hidden="true" />
              </button>
            ) : null}
          </span>
        );
      }
      if (field === STUDY_MGMT_SITES_COLUMN) {
        const studyId = getStudyRowId(row);
        return (
          <StudySitesCell
            sites={studyEnrichment.sitesByStudyId[studyId] || []}
            loading={Boolean(studyEnrichment.pendingStudyIds?.[studyId])}
          />
        );
      }
      return String(row[field] ?? "—");
    },
    [
      canAddEditSchedule,
      canAddStudy,
      handleOpenGateAdvance,
      handleOpenSchedule,
      handleOpenStudyFromCell,
      handleOpenUsersEdit,
      handleStatusChange,
      studyEnrichment,
    ],
  );

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) return true;
    if (
      field === "GroupType" ||
      field === STUDY_MGMT_PI_COLUMN ||
      field === "SystemDateTime" ||
      field === "StudyStatus" ||
      field === STUDY_MGMT_GATE_COLUMN ||
      field === STUDY_MGMT_SITES_COLUMN ||
      field === STUDY_MGMT_USERS_COLUMN
    ) {
      return true;
    }

    if (field === STUDY_MGMT_ANCHOR_COLUMN) {
      const number = formatStudyNumberLabel(row).toLowerCase();
      const name = String(row.StudyName ?? "").trim().toLowerCase();
      return number.includes(term) || name.includes(term);
    }

    return String(row[field] ?? "")
      .toLowerCase()
      .includes(term);
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "GroupType") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={groupTypeFilterOptions}
            value={tableFilters.groupTypes}
            onChange={(values) => updateTableFilters({ groupTypes: values })}
            emptyLabel="All groups"
            addLabel="+"
            popoverTitle="Filter by group type"
            popoverSubtitle="Select one or more group types"
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === STUDY_MGMT_PI_COLUMN) {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={piFilterOptions}
            value={tableFilters.piNames}
            onChange={(values) => updateTableFilters({ piNames: values })}
            emptyLabel="All PI"
            addLabel="+"
            popoverTitle="Filter by PI"
            popoverSubtitle="Select one or more principal investigators"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "SystemDateTime") {
        if (options?.checkOnly) return true;
        return (
          <DateRangeFilter
            value={tableFilters.createdDateRange}
            onChange={(range) => updateTableFilters({ createdDateRange: range })}
            placeholder="Creation date range"
            presetOptions={STUDY_MGMT_COLUMN_DATE_PRESETS}
            compact
            disabled={options?.disabled}
          />
        );
      }

      if (field === "StudyStatus") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search report-table__column-select"
            value={tableFilters.status}
            disabled={options?.disabled}
            onChange={(event) => updateTableFilters({ status: event.target.value })}
          >
            <option value="">All</option>
            {statusFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === STUDY_MGMT_GATE_COLUMN) {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={gateFilterOptions}
            value={tableFilters.gateCodes}
            onChange={(values) => updateTableFilters({ gateCodes: values })}
            emptyLabel="All gates"
            addLabel="+"
            popoverTitle="Filter by gate"
            popoverSubtitle="Select one or more study gates"
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === STUDY_MGMT_USERS_COLUMN || field === STUDY_MGMT_SITES_COLUMN) {
        if (options?.checkOnly) return true;
        return null;
      }

      return null;
    },
    [
      gateFilterOptions,
      groupTypeFilterOptions,
      piFilterOptions,
      statusFilterOptions,
      tableFilters,
      updateTableFilters,
    ],
  );

  const layoutStorageKey = useMemo(
    () =>
      userData?.OrganizationId
        ? `study-mgmt-report-layout-v6-${userData.OrganizationId}`
        : "",
    [userData?.OrganizationId],
  );

  if (!userData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "12.5rem" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading session...</span>
        </div>
      </div>
    );
  }

  if (!canViewStudyList) {
    return <Navigate to="/notification-center" replace />;
  }

  return (
    <div className="study-mgmt-page study-mgmt-list-page">
      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Study Management" },
        ]}
        actions={[
          { icon: "fas fa-refresh", label: "Refresh", onClick: handleRefresh },
          ...(canAddStudy
            ? [
                {
                  icon: "fas fa-plus",
                  label: "Add Study",
                  onClick: () => handleEdit(null),
                },
              ]
            : []),
          ...(canCreateCapa
            ? [
                {
                  icon: "fas fa-clipboard-list",
                  label: "Create CAPA",
                  onClick: () => handleCreateCapa(),
                },
              ]
            : []),
        ]}
      />

      <div className="study-mgmt-report-wrap">
        <ReportTable
          rows={filteredStudies}
          loading={studiesLoading}
          fieldOptions={STUDY_MGMT_FIELD_OPTIONS}
          defaultColumns={DEFAULT_STUDY_MGMT_COLUMNS}
          getFieldLabel={getStudyMgmtFieldLabel}
          renderCell={renderReportCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          frozenColumnWidthRem={14}
          hideAddColumnFields={STUDY_MGMT_LOCKED_COLUMNS}
          hideColumnHeaderActionsFields={STUDY_MGMT_LOCKED_COLUMNS}
          persistLayoutKey={layoutStorageKey}
        />
      </div>

      {isStudyPopupShow &&
        createPortal(
          <div
            className={`modal fade show d-block study-workspace-modal${
              isStudyModalReadOnly ? " study-workspace-modal--view" : ""
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="study-add-edit-title"
          >
            <div className="modal-dialog study-add-edit-modal">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="study-add-edit-title">
                    {studyModalTitle}
                  </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                ></button>
              </div>

              <div className="modal-body study-modal-body--sections">
                {modalStudyArchived ? (
                  <div className="study-archived-alert" role="status">
                    <i className="fas fa-archive" aria-hidden="true" />
                    <span>{ARCHIVED_STUDY_READONLY_MESSAGE}</span>
                  </div>
                ) : null}
                <div className="study-modal-panel">
                <StudyFormSection title="Basic Information">
                  <div className="study-form-grid study-form-grid--pair">
                    <div className="study-form-field">
                      <label>
                        Study Number <span className="mandatory">*</span>
                      </label>
                      <div className="span-under-input study-form-prefix-input">
                        <span>{formData.prefix}</span>
                        <input
                          type="text"
                          className="form-control"
                          name="studyNumber"
                          value={formData.studyNumber || ""}
                          onChange={handleFormInput}
                          disabled={isStudyModalReadOnly}
                          readOnly={isStudyModalReadOnly}
                        />
                      </div>
                    </div>
                    <div className="study-form-field">
                      <label>
                        Study Title <span className="mandatory">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        onChange={handleFormInput}
                        name="studyTitle"
                        value={formData.studyTitle || ""}
                        disabled={isStudyModalReadOnly}
                        readOnly={isStudyModalReadOnly}
                      />
                    </div>
                    <div className="study-form-field">
                      <label>
                        Study Color Code <span className="mandatory">*</span>
                      </label>
                      <Dropdown
                        options={studyColors}
                        isMulti={false}
                        isColorDropDown={true}
                        selected={formData.studyColor}
                        onSelect={handleStudyColors}
                        disabled={isStudyModalReadOnly}
                      />
                    </div>
                    <div className="study-form-field">
                      <label>Status</label>
                      <Dropdown
                        options={studyStatusDropdownOptions}
                        isMulti={false}
                        selected={formData.status}
                        onSelect={handleStatus}
                        disabled={isStudyModalReadOnly}
                      />
                    </div>
                    <div className="study-form-field">
                      <label>Department</label>
                      <Dropdown
                        options={department}
                        isMulti={false}
                        selected={departmentSelected}
                        onSelect={handleDepartment}
                        disabled={isStudyModalReadOnly}
                      />
                    </div>
                    <StudyTypeField
                      values={formData}
                      onChange={handleStudyMetadataChange}
                      disabled={isStudyModalReadOnly}
                    />
                    <StudyStartDateField
                      values={formData}
                      onChange={handleStudyMetadataChange}
                      isCreating={!formData.studyId}
                      disabled={isStudyModalReadOnly}
                    />
                    <StudyEndDateField
                      values={formData}
                      onChange={handleStudyMetadataChange}
                      disabled={isStudyModalReadOnly}
                    />
                    <StudyProtocolNumberField
                      values={formData}
                      onChange={handleStudyMetadataChange}
                      disabled={isStudyModalReadOnly}
                    />
                    <StudyProtocolVersionField
                      values={formData}
                      onChange={handleStudyMetadataChange}
                      disabled={isStudyModalReadOnly}
                    />
                    <StudyDescriptionField
                      values={formData}
                      onChange={handleStudyMetadataChange}
                      disabled={isStudyModalReadOnly}
                    />
                  </div>
                </StudyFormSection>

                <StudyFormSection
                  title="User & Site Information"
                  className="study-modal-fold--users"
                >
                  <div className="study-form-grid study-form-grid--pair">
                    <div className="study-form-field">
                      <label>
                        Sites <span className="mandatory">*</span>
                      </label>
                      <Dropdown
                        options={siteOptions}
                        isMulti={true}
                        selected={formData.selectedSites || []}
                        onSelect={handleSites}
                        disabled={isStudyModalReadOnly}
                      />
                    </div>
                    <div className="study-form-field">
                      <label>
                        Principal Investigator <span className="mandatory">*</span>
                      </label>
                      <Dropdown
                        options={principalInvestigators}
                        isMulti={false}
                        selected={formData.principalInvestigator}
                        onSelect={handlePI}
                        disabled={isStudyModalReadOnly}
                      />
                    </div>
                  </div>

                  <div className="study-modal-fold__staff-panel">
                    <StudyAssignedStaffPanel
                      staffRows={siteFilteredStaff}
                      selectedSites={formData.selectedSites || []}
                      assignedStaffIds={assignedStaffIds}
                      onToggleStaff={
                        isStudyModalReadOnly ? undefined : handleToggleStudyStaff
                      }
                      hasSitesSelected={selectedSiteIds.length > 0}
                      readOnly={isStudyModalReadOnly}
                    />
                  </div>
                </StudyFormSection>

                <StudyFormSection
                  title="Study Meta Information"
                  className="study-modal-fold--meta"
                >
                  <div className="study-form-grid study-form-grid--pair">
                    <div className="study-form-field">
                      <label>Eudract Number</label>
                      <input
                        type="text"
                        className="form-control"
                        onChange={handleFormInput}
                        name="eudractNumber"
                        value={formData.eudractNumber || ""}
                        disabled={isStudyModalReadOnly}
                        readOnly={isStudyModalReadOnly}
                      />
                    </div>
                    <div className="study-form-field">
                      <label>R&amp;D Number</label>
                      <input
                        type="text"
                        className="form-control"
                        onChange={handleFormInput}
                        name="rdNumber"
                        value={formData.rdNumber || ""}
                        disabled={isStudyModalReadOnly}
                        readOnly={isStudyModalReadOnly}
                      />
                    </div>
                    <div className="study-form-field">
                      <label>Sponsor Name</label>
                      <input
                        type="text"
                        className="form-control"
                        onChange={handleFormInput}
                        name="sponsorName"
                        value={formData.sponsorName || ""}
                        disabled={isStudyModalReadOnly}
                        readOnly={isStudyModalReadOnly}
                      />
                    </div>
                    <div className="study-form-field">
                      <label>IRAS Number</label>
                      <input
                        type="text"
                        className="form-control"
                        onChange={handleFormInput}
                        name="irasNumber"
                        value={formData.irasNumber || ""}
                        disabled={isStudyModalReadOnly}
                        readOnly={isStudyModalReadOnly}
                      />
                    </div>
                  </div>

                  <div className="study-modal-coordinator-card">
                    <h6 className="study-modal-coordinator-card__title">Coordinator</h6>
                    <div className="study-form-grid study-form-grid--pair">
                      <div className="study-form-field">
                        <label>Coordinator Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="cordinatorName"
                          value={formData.cordinatorName || ""}
                          onChange={handleFormInput}
                          disabled={isStudyModalReadOnly}
                          readOnly={isStudyModalReadOnly}
                        />
                      </div>
                      <div className="study-form-field">
                        <label>Coordinator Contact</label>
                        <input
                          type="text"
                          className="form-control"
                          name="cordinatorContact"
                          value={formData.cordinatorContact || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setFormData((prev) => ({
                                ...prev,
                                cordinatorContact: value,
                              }));
                            }
                          }}
                          maxLength={11}
                          disabled={isStudyModalReadOnly}
                          readOnly={isStudyModalReadOnly}
                        />
                      </div>
                      <div className="study-form-field study-form-field--span-row">
                        <label>Coordinator Email</label>
                        <input
                          type="text"
                          className="form-control"
                          name="cordinatorEmail"
                          value={formData.cordinatorEmail || ""}
                          onChange={handleFormInput}
                          disabled={isStudyModalReadOnly}
                          readOnly={isStudyModalReadOnly}
                        />
                      </div>
                    </div>
                  </div>
                </StudyFormSection>
                </div>
              </div>

              <div className="modal-footer">
                <div className="row">
                  <div className="col-md-12">
                    <button
                      className="btn btn-outline-primary me-2"
                      onClick={handleClose}
                    >
                      <i className="fas fa-close me-2"></i>
                      {isStudyModalReadOnly ? "Close" : "Cancel"}
                    </button>
                    {isStudyModalReadOnly &&
                    canAddStudy &&
                    formData.studyId &&
                    !modalStudyArchived ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleStudyModalEnableEdit}
                      >
                        <i className="fas fa-edit me-2"></i>
                        Edit
                      </button>
                    ) : null}
                    {!isStudyModalReadOnly ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUserSaveUpdate}
                      >
                        {!formData.studyId ? (
                          <>
                            <i className="fas fa-plus me-2"></i>
                            Add
                          </>
                        ) : (
                          <>
                            <i className="fas fa-edit me-2"></i>
                            Update
                          </>
                        )}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
          document.body,
        )}

      <CapaCreateModal
        open={showCapaCreateModal}
        context={capaCreateContext}
        titleId="study-capa-create-title"
        onClose={() => {
          setShowCapaCreateModal(false);
          setCapaCreateContext(null);
        }}
        onSaved={() => {
          setShowCapaCreateModal(false);
          setCapaCreateContext(null);
          showAlert({
            title: "CAPA created",
            message: "Record has been saved successfully.",
            type: "success",
          });
        }}
      />

      <StudyGateAdvanceModal
        open={Boolean(gateAdvanceStudy)}
        onClose={() => setGateAdvanceStudy(null)}
        studyId={getStudyRowId(gateAdvanceStudy)}
        studyLabel={gateAdvanceStudy?.StudyName || ""}
        currentGateCode={getStudyGateCode(
          gateAdvanceStudy,
          studyEnrichment.gateByStudyId,
        )}
        siteIds={gateAdvanceSiteIds}
        templateCategory={DEFAULT_GATE_ADVANCE_TEMPLATE_CATEGORY}
        userData={userData}
        onAdvanced={handleGateAdvanced}
      />

      <StudyUsersEditModal
        open={Boolean(usersEditStudy)}
        studyRow={usersEditStudy}
        userData={userData}
        studyStatusOptions={studyStatus}
        studyColorOptions={studyColors}
        onClose={() => setUsersEditStudy(null)}
        onSaved={() => {
          setUsersEditStudy(null);
          reloadStudiesRef.current();
        }}
        showAlert={showAlert}
        showLoader={showLoader}
        hideLoader={hideLoader}
      />

      <StudyArchiveWizard
        open={Boolean(archiveStudy)}
        studyRow={archiveStudy}
        userData={userData}
        siteIds={archiveWizardSiteIds}
        scheduleStatusByStudyId={studyEnrichment.scheduleStatusByStudyId}
        showAlert={showAlert}
        onClose={() => setArchiveStudy(null)}
        onArchived={handleArchiveCompleted}
      />

      <ScheduleStartChoiceModal
        open={Boolean(scheduleStartStudy)}
        targetStudy={scheduleStartStudy}
        cloneableStudies={cloneableScheduleStudies}
        scheduleStatusByStudyId={studyEnrichment.scheduleStatusByStudyId}
        loading={scheduleCloneLoading}
        error={scheduleCloneError}
        organizationId={userData?.OrganizationId}
        createdBy={userData?.StaffId}
        onClose={() => {
          if (scheduleCloneLoading) return;
          setScheduleStartStudy(null);
          setScheduleCloneError("");
        }}
        onStartFromScratch={handleScheduleStartFromScratch}
        onClone={handleScheduleClone}
      />

      <ScheduleWorkspaceModal
        open={Boolean(scheduleWorkspace)}
        scheduleId={scheduleWorkspace?.scheduleId}
        studyRow={scheduleWorkspace?.studyRow}
        onClose={() => {
          setScheduleWorkspace(null);
          reloadStudiesRef.current({ silent: true });
        }}
      />
    </div>
  );
};

export default StudyManagement;
